import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, Observable, tap, throwError } from 'rxjs';

import { ServiceResponseV1 } from '@core/models/apis';
import { OrderAdminListQuery, OrderWithItemsV1 } from '@core/models/order-admin';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly ordersAdminUrl = `${environment.apiUrl}${environment.apis.ordersEngineAdmin}/v1/orders`;

  private readonly orders$ = signal<OrderWithItemsV1[]>([]);
  readonly orders = this.orders$.asReadonly();

  private readonly ordersById$ = signal<Record<string, OrderWithItemsV1>>({});
  readonly ordersById = this.ordersById$.asReadonly();

  getOrdersList(query: OrderAdminListQuery = {}): Observable<ServiceResponseV1<OrderWithItemsV1[]>> {
    let params = new HttpParams();
    if (query.pageNum !== undefined) params = params.set('pageNum', String(query.pageNum));
    if (query.pageSize !== undefined) params = params.set('pageSize', String(query.pageSize));
    if (query.start) params = params.set('start', query.start);
    if (query.end) params = params.set('end', query.end);
    if (query.status) params = params.set('status', query.status);
    if (query.orderType) params = params.set('orderType', query.orderType);

    return this.http.get<ServiceResponseV1<OrderWithItemsV1[]>>(this.ordersAdminUrl, { params }).pipe(
      tap((response) => {
        const list = (response.data ?? []).map((o) => ({
          ...o,
          amountPaid: o.amountPaid != null ? o.amountPaid / 100 : null
        }));
        this.orders$.set(list);
        this.ordersById$.set(Object.fromEntries(list.map((o) => [o.orderId, o])));
      }),
      catchError((error) => this.errorHandler(error, 'Error getting orders list'))
    );
  }

  private errorHandler(error: unknown, serviceMessage: string): Observable<never> {
    const err = error as { error?: { message?: string }; message?: string };
    const message = error instanceof Error ? error.message : (err?.error?.message ?? err?.message ?? 'Unknown error');
    console.error(`[OrdersService] ${serviceMessage}, ${message}`);
    this.messageService.add({ severity: 'error', summary: 'Error', detail: serviceMessage });
    return throwError(() => error);
  }
}
